import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { ApiError } from '../../lib/api';
import type { Campaign } from '../../types/campaign';
import type { PromotionPlan } from '../../types/promotionPlan';
import {
  attachCampaign,
  detachCampaign,
  listBrandCampaigns,
  reorderCampaigns,
} from './api';
import { reorderIds } from './taskOrdering';

// Platform indicators (spec §2.1) derived honestly from the Campaign's
// authored content — this codebase has no "Local" concept at the Campaign
// level (local assets are row-scoped on the Rollout), so we only surface the
// signals that actually exist: Meta when any Meta ad copy is authored,
// Google when any Google asset is. Empty Campaign → no chips.
function campaignPlatforms(c: Campaign): string[] {
  const out: string[] = [];
  const m = c.metaAdCopy;
  if (
    m &&
    ((m.headlines?.length ?? 0) > 0 ||
      (m.bodyCopy?.length ?? 0) > 0 ||
      (m.descriptions?.length ?? 0) > 0)
  ) {
    out.push('Meta');
  }
  const g = c.googleAssets;
  if (g && ((g.headlines?.length ?? 0) > 0 || (g.descriptions?.length ?? 0) > 0)) {
    out.push('Google');
  }
  return out;
}

export function CampaignsAttachedSection({ plan }: { plan: PromotionPlan }) {
  const qc = useQueryClient();
  const [showPicker, setShowPicker] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [detachTarget, setDetachTarget] = useState<Campaign | string | null>(
    null,
  );

  const brandCampaigns = useQuery({
    queryKey: ['plan-brand-campaigns', plan.brandAccountId],
    queryFn: () => listBrandCampaigns(plan.brandAccountId),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['plan', plan.id] });

  const detachMut = useMutation({
    mutationFn: (campaignId: string) => detachCampaign(plan.id, campaignId),
    onSuccess: invalidate,
  });

  const reorderMut = useMutation({
    mutationFn: (campaignIds: string[]) =>
      reorderCampaigns(plan.id, campaignIds),
    onSuccess: invalidate,
  });

  const byId = useMemo(() => {
    const m = new Map<string, Campaign>();
    for (const c of brandCampaigns.data ?? []) m.set(c.id, c);
    return m;
  }, [brandCampaigns.data]);

  if (brandCampaigns.isLoading) return <Spinner />;
  if (brandCampaigns.error) {
    return (
      <p className="text-sm text-red-600">
        Failed to load Campaigns:{' '}
        {(brandCampaigns.error as Error).message}
      </p>
    );
  }

  const attachedIds = plan.campaignIds;
  const busy = detachMut.isPending || reorderMut.isPending;

  const handleDrop = (targetId: string) => {
    if (!dragId) return;
    const next = reorderIds(attachedIds, dragId, targetId);
    setDragId(null);
    const changed =
      next.length !== attachedIds.length ||
      next.some((id, i) => id !== attachedIds[i]);
    if (changed) reorderMut.mutate(next);
  };

  return (
    <div>
      <div className="mb-3">
        <Button type="button" onClick={() => setShowPicker(true)}>
          + Attach Campaign
        </Button>
      </div>

      {attachedIds.length === 0 ? (
        <p className="text-sm text-slate-400 italic">
          No Campaigns attached yet. Click + Attach Campaign to add one.
        </p>
      ) : (
        <ol className="space-y-2">
          {attachedIds.map((cid, index) => {
            const c = byId.get(cid) ?? null;
            const platforms = c ? campaignPlatforms(c) : [];
            return (
              <li
                key={cid}
                draggable
                onDragStart={() => setDragId(cid)}
                onDragEnd={() => setDragId(null)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleDrop(cid);
                }}
                className={`group flex items-center gap-3 rounded-md border bg-white p-3 ${
                  dragId === cid
                    ? 'border-slate-400 opacity-60'
                    : 'border-slate-200'
                }`}
              >
                <span
                  aria-hidden
                  className="cursor-grab select-none text-slate-300"
                  title="Drag to reorder"
                >
                  ⠿
                </span>
                <span className="w-5 text-sm tabular-nums text-slate-400">
                  {index + 1}.
                </span>
                <div className="min-w-0 flex-1">
                  {c ? (
                    <Link
                      to={`/campaigns/${c.id}`}
                      className="text-sm font-medium text-slate-900 hover:underline"
                    >
                      {c.name}
                    </Link>
                  ) : (
                    <span
                      className="text-sm font-medium text-slate-500"
                      title={cid}
                    >
                      (Campaign unavailable)
                    </span>
                  )}
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                    {c && (
                      <span className="rounded bg-slate-100 px-1.5 py-0.5">
                        {c.status}
                      </span>
                    )}
                    {platforms.map((p) => (
                      <span
                        key={p}
                        className="rounded bg-slate-100 px-1.5 py-0.5"
                      >
                        {p}
                      </span>
                    ))}
                    {c && platforms.length === 0 && (
                      <span className="italic text-slate-400">
                        no content yet
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={busy}
                  aria-label={`Detach ${c?.name ?? 'campaign'}`}
                  title="Detach"
                  onClick={() => setDetachTarget(c ?? cid)}
                  className="px-2 text-slate-300 hover:text-red-600 group-hover:text-slate-500 disabled:opacity-50"
                >
                  ✕
                </button>
              </li>
            );
          })}
        </ol>
      )}

      {showPicker && (
        <CampaignPickerModal
          planId={plan.id}
          brandCampaigns={brandCampaigns.data ?? []}
          attachedIds={attachedIds}
          onClose={() => setShowPicker(false)}
          onAttached={() => {
            setShowPicker(false);
            invalidate();
          }}
        />
      )}

      {detachTarget !== null && (
        <DetachConfirmModal
          name={
            typeof detachTarget === 'string'
              ? null
              : detachTarget.name
          }
          busy={detachMut.isPending}
          onCancel={() => setDetachTarget(null)}
          onConfirm={() => {
            const id =
              typeof detachTarget === 'string'
                ? detachTarget
                : detachTarget.id;
            setDetachTarget(null);
            detachMut.mutate(id);
          }}
        />
      )}
    </div>
  );
}

function CampaignPickerModal({
  planId,
  brandCampaigns,
  attachedIds,
  onClose,
  onAttached,
}: {
  planId: string;
  brandCampaigns: Campaign[];
  attachedIds: string[];
  onClose: () => void;
  onAttached: () => void;
}) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const attached = useMemo(() => new Set(attachedIds), [attachedIds]);
  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return brandCampaigns
      .filter((c) => !attached.has(c.id))
      .filter((c) => (q ? c.name.toLowerCase().includes(q) : true));
  }, [brandCampaigns, attached, search]);

  // Attach is idempotent server-side; we still send the picked set in the
  // chosen order so campaignIds grows predictably. Sequential to keep the
  // append order deterministic (parallel writes would race the array).
  const mut = useMutation({
    mutationFn: async () => {
      setError(null);
      for (const id of brandCampaigns
        .filter((c) => selected.has(c.id))
        .map((c) => c.id)) {
        await attachCampaign(planId, id);
      }
    },
    onSuccess: onAttached,
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : String(err)),
  });

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[80vh] w-full max-w-lg flex-col rounded-md border border-slate-200 bg-white shadow-xl">
        <div className="border-b border-slate-100 p-4">
          <h3 className="text-sm font-semibold text-slate-900">
            Attach Campaigns
          </h3>
          <input
            type="text"
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Campaigns…"
            className="mt-3 w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {candidates.length === 0 ? (
            <p className="p-4 text-center text-sm text-slate-400">
              {brandCampaigns.length === 0
                ? 'No Campaigns exist for this brand yet.'
                : 'No matching Campaigns (all may already be attached).'}
            </p>
          ) : (
            <ul>
              {candidates.map((c) => {
                const platforms = campaignPlatforms(c);
                return (
                  <li key={c.id}>
                    <label className="flex cursor-pointer items-start gap-3 rounded p-2 hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={selected.has(c.id)}
                        onChange={() => toggle(c.id)}
                        className="mt-1 h-4 w-4"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-slate-900">
                          {c.name}
                        </span>
                        <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                          <span className="rounded bg-slate-100 px-1.5 py-0.5">
                            {c.status}
                          </span>
                          {platforms.map((p) => (
                            <span
                              key={p}
                              className="rounded bg-slate-100 px-1.5 py-0.5"
                            >
                              {p}
                            </span>
                          ))}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {error && (
          <p className="px-4 text-sm text-red-600">{error}</p>
        )}

        <div className="flex items-center justify-between gap-2 border-t border-slate-100 p-4">
          <span className="text-xs text-slate-500">
            {selected.size} selected
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={mut.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => mut.mutate()}
              disabled={mut.isPending || selected.size === 0}
            >
              {mut.isPending ? 'Attaching…' : 'Attach'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetachConfirmModal({
  name,
  busy,
  onCancel,
  onConfirm,
}: {
  name: string | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-md border border-slate-200 bg-white p-5 shadow-xl">
        <h3 className="text-sm font-semibold text-slate-900">
          Detach {name ? `"${name}"` : 'this Campaign'}?
        </h3>
        <p className="mt-2 text-sm text-slate-600">
          This Campaign will be removed from the Plan but not deleted. If a
          Rollout already exists, the Campaign's rows in that Rollout are
          unaffected.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            variant="destructive"
          >
            Detach
          </Button>
        </div>
      </div>
    </div>
  );
}
